export interface CerereFunctieAssignment {
  id?: number;
  cerere_id: number;
  functie_id: number;
  directie_id: number;
  compartiment_id?: number;
  user_id?: number;
  order_priority: number;
  is_draft: boolean;
  assigned_date?: string;
  assigned_by_user_id?: number;
  assignment_status: AssignmentStatus;
  created_at?: string;
  updated_at?: string;
  
  // Enriched data from API
  functie?: FunctieData;
  directie?: DirectieData;
  compartiment?: CompartimentData;
  user?: UserData;
}

export type AssignmentStatus = 'draft' | 'active' | 'assigned' | 'completed';

export interface FunctieData {
  id: number;
  name: string;
  slug: string;
  acronym?: string;
  active: boolean;
  public_visible: boolean;
  observations?: string;
}

export interface DirectieData {
  id_directie: number;
  name: string;
  acronym?: string;
  active: boolean;
  compartimente?: CompartimentData[];
}

export interface CompartimentData {
  id_compartiment: number;
  name: string;
  directie_id: number;
  active: boolean;
}

export interface UserData {
  id_user: number;
  id: number; // Some APIs may return both formats
  full_name: string;
  name?: string;
  email: string;
  functii?: FunctieData[];
  display_text?: string;
}

export interface CerereFunctieAssignmentRequest {
  assignments: {
    functie_id: number;
    directie_id: number;
    compartiment_id?: number;
    user_id?: number;
    order_priority: number;
  }[];
}

export interface AssignmentSummary {
  has_assignments: boolean;
  summary: string;
  count: number;
  assignments: CerereFunctieAssignment[];
  statistics: AssignmentStatistics;
}

export interface AssignmentStatistics {
  total: number;
  draft: number;
  active: number;
  assigned: number;
  completed: number;
}

export interface UserAssignmentFilter {
  directie_id: number;
  compartiment_id?: number;
}

export interface CerereAssignmentDialogData {
  cerereId: number;
  cerereNumber: string;
  existingAssignments: CerereFunctieAssignment[];
  readOnly?: boolean;
}

export interface AssignmentFormData {
  functie_id: number | null;
  directie_id: number | null;
  compartiment_id: number | null;
  user_id: number | null;
  order_priority: number;
  assignment_status: AssignmentStatus;
}

export interface AvailableSelectionsData {
  functii: FunctieData[];
  directii: DirectieData[];
  compartimente: CompartimentData[];
  users: UserData[];
}

export interface AssignmentValidationError {
  index?: number;
  field?: string;
  message: string;
}

export interface AssignmentUpdateResult {
  success: CerereFunctieAssignment[];
  errors: { [index: number]: string[] };
  created_count: number;
  updated_count: number;
}

export interface DragDropItem {
  previousIndex: number;
  currentIndex: number;
}

export interface AssignmentInitializationResult {
  success: boolean;
  message: string;
  assignments: CerereFunctieAssignment[];
}

export interface AssignmentWorkflowSuggestion {
  type: 'initialize' | 'assign_users' | 'convert_drafts';
  title: string;
  description: string;
  action: string;
  priority: 'high' | 'medium' | 'low';
}

export interface UserAssignmentRequest {
  user_id: number;
  assigned_by_user_id: number;
}

export interface AssignmentDisplayConfig {
  showStatus: boolean;
  showDraftBadge: boolean;
  showUserAvatars: boolean;
  compactMode: boolean;
  allowEdit: boolean;
  allowAssignment: boolean;
}

export interface AssignmentNotification {
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  assignment?: CerereFunctieAssignment;
  duration?: number;
}

// Status display helpers
export const AssignmentStatusTexts: Record<AssignmentStatus, string> = {
  draft: 'Ciornă',
  active: 'Activ',
  assigned: 'Asignat',
  completed: 'Finalizat'
};

export const AssignmentStatusClasses: Record<AssignmentStatus, string> = {
  draft: 'badge-secondary',
  active: 'badge-primary', 
  assigned: 'badge-success',
  completed: 'badge-info'
};

export const AssignmentStatusColors: Record<AssignmentStatus, string> = {
  draft: '#6c757d',
  active: '#007bff',
  assigned: '#28a745', 
  completed: '#17a2b8'
};